import React, { useState, useEffect } from 'react';
import { TrendingUp, Download, Calendar, Users, BarChart3, PieChart, FileText, FileSpreadsheet, CalendarRange, ListChecks, ClipboardList, User, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { programsAPI, athletesAPI, attendanceAPI } from '../services/api';
import { AthleteAvatar } from '../components/AthleteAvatar';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hoop-tracker-7/artifacts/tisywt9m_attendance%20tracker%20logo.png';

// Helper: load image as base64 for PDF embedding
const loadImageAsBase64 = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

const Reports = () => {
  const [programs, setPrograms] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [dateRange, setDateRange] = useState('7');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [reportType, setReportType] = useState('summary'); // 'summary' or 'detailed' or 'athlete'
  const [includeNotes, setIncludeNotes] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState('all');
  const [athleteSearchOpen, setAthleteSearchOpen] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);

  // Helper: add logo to PDF and return the Y position after logo
  const addLogoToPdf = (doc, startX = 14, startY = 8) => {
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', startX, startY, 28, 28);
      return startY + 6; // text starts beside the logo
    }
    return startY;
  };

  useEffect(() => {
    fetchData();
    loadImageAsBase64(LOGO_URL).then(setLogoBase64);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [programsRes, athletesRes, attendanceRes] = await Promise.all([
        programsAPI.getAll(),
        athletesAPI.getAll(),
        attendanceAPI.getAll()
      ]);
      
      setPrograms(programsRes.data);
      setAthletes(athletesRes.data);
      setAttendance(attendanceRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAttendance = () => {
    let filtered = attendance;
    
    if (selectedProgram !== 'all') {
      filtered = filtered.filter(a => a.programId === selectedProgram);
    }

    if (reportType === 'athlete' && selectedAthlete !== 'all') {
      filtered = filtered.filter(a => a.athleteId === selectedAthlete);
    }
    
    if (dateRange === 'custom') {
      // Custom date range
      if (customStartDate && customEndDate) {
        const startDate = new Date(customStartDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter(a => {
          const recordDate = new Date(a.date);
          return recordDate >= startDate && recordDate <= endDate;
        });
      }
    } else {
      // Preset date ranges
      const daysAgo = parseInt(dateRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      filtered = filtered.filter(a => new Date(a.date) >= cutoffDate);
    }
    
    return filtered;
  };

  const getDateRangeLabel = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return `${customStartDate} to ${customEndDate}`;
    }
    const labels = {
      '7': 'Last 7 days',
      '14': 'Last 14 days',
      '30': 'Last 30 days',
      '90': 'Last 90 days',
      '180': 'Last 6 months',
      '365': 'Last year'
    };
    return labels[dateRange] || `Last ${dateRange} days`;
  };

  const calculateStats = () => {
    const filtered = getFilteredAttendance();
    const present = filtered.filter(a => a.status === 'present').length;
    const absent = filtered.filter(a => a.status === 'absent').length;
    const late = filtered.filter(a => a.status === 'late').length;
    const excused = filtered.filter(a => a.status === 'excused').length;
    const total = filtered.length;
    const uniqueAthletes = new Set(filtered.map(a => a.athleteId)).size;
    
    return {
      present,
      absent,
      late,
      excused,
      total,
      uniqueAthletes,
      attendanceRate: total > 0 ? Math.round(((present + late) / total) * 100) : 0
    };
  };

  const getAthleteStats = () => {
    const filtered = getFilteredAttendance();
    const athleteMap = {};
    
    filtered.forEach(record => {
      if (!athleteMap[record.athleteId]) {
        athleteMap[record.athleteId] = {
          present: 0,
          absent: 0,
          late: 0,
          excused: 0,
          total: 0
        };
      }
      athleteMap[record.athleteId][record.status]++;
      athleteMap[record.athleteId].total++;
    });
    
    return Object.entries(athleteMap).map(([athleteId, stats]) => {
      const athlete = athletes.find(a => a.id === athleteId);
      return {
        ...athlete,
        ...stats,
        rate: Math.round(((stats.present + stats.late) / stats.total) * 100)
      };
    }).sort((a, b) => {
      const lastA = (a.name || '').trim().split(/\s+/).pop().toLowerCase();
      const lastB = (b.name || '').trim().split(/\s+/).pop().toLowerCase();
      return lastA.localeCompare(lastB);
    });
  };

  // Get detailed attendance records grouped by date
  const getDetailedAttendance = () => {
    const filtered = getFilteredAttendance();
    
    // Get unique dates sorted in descending order
    const uniqueDates = [...new Set(filtered.map(r => r.date))].sort((a, b) => new Date(b) - new Date(a));
    
    // Group attendance by date
    const byDate = {};
    uniqueDates.forEach(date => {
      byDate[date] = filtered.filter(r => r.date === date);
    });
    
    return { uniqueDates, byDate };
  };

  // Get status label and color
  const getStatusDisplay = (status) => {
    const statusMap = {
      present: { label: 'Present', color: '#007A3D', bg: 'bg-green-100 text-green-800' },
      absent: { label: 'Absent', color: '#CE1126', bg: 'bg-red-100 text-red-800' },
      late: { label: 'Late', color: '#FCD116', bg: 'bg-yellow-100 text-yellow-800' },
      excused: { label: 'Excused', color: '#0067B9', bg: 'bg-blue-100 text-blue-800' }
    };
    return statusMap[status] || { label: status, color: '#666', bg: 'bg-gray-100 text-gray-800' };
  };

  const handleExport = (format = 'csv') => {
    const stats = calculateStats();
    const athleteStats = getAthleteStats();
    const programName = selectedProgram === 'all' ? 'All Programs' : programs.find(p => p.id === selectedProgram)?.name;
    const dateStr = new Date().toISOString().split('T')[0];
    const dateRangeLabel = getDateRangeLabel();
    const { uniqueDates, byDate } = getDetailedAttendance();
    
    if (format === 'csv') {
      let csvContent = '';
      
      if (reportType === 'summary' || reportType === 'grid') {
        // Summary/Grid Report CSV - grid format
        const filtered = getFilteredAttendance();
        const dates = [...new Set(filtered.map(r => r.date))].sort((a, b) => new Date(a) - new Date(b));
        const statusMap = {};
        filtered.forEach(r => {
          if (!statusMap[r.athleteId]) statusMap[r.athleteId] = {};
          statusMap[r.athleteId][r.date] = r.status;
        });
        const statusLetter = (s) => s === 'present' ? 'P' : s === 'absent' ? 'A' : s === 'late' ? 'L' : s === 'excused' ? 'E' : '';

        csvContent = 'Attendance Summary Report\n';
        csvContent += `Program: ${programName}\n`;
        csvContent += `Date Range: ${dateRangeLabel}\n\n`;

        // Header row
        csvContent += `Athlete,Present,Absent,Late,Excused,%,${dates.join(',')}\n`;

        // Athlete rows
        athleteStats.forEach(athlete => {
          const ad = statusMap[athlete.id] || {};
          const dateCells = dates.map(d => statusLetter(ad[d])).join(',');
          csvContent += `"${athlete.name}",${athlete.present},${athlete.absent},${athlete.late},${athlete.excused},${athlete.rate},${dateCells}\n`;
        });
      } else {
        // Detailed Report CSV
        csvContent = 'Detailed Attendance Report\n\n';
        csvContent += `Program: ${programName}\n`;
        csvContent += `Date Range: ${dateRangeLabel}\n\n`;
        
        const headers = includeNotes 
          ? 'Date,Athlete,Status,Notes\n'
          : 'Date,Athlete,Status\n';
        csvContent += headers;
        
        uniqueDates.forEach(date => {
          const records = byDate[date];
          records.forEach(record => {
            const athlete = athletes.find(a => a.id === record.athleteId);
            const athleteName = athlete?.name || 'Unknown';
            const status = getStatusDisplay(record.status).label;
            
            if (includeNotes) {
              const notes = (record.notes || '').replace(/"/g, '""'); // Escape quotes
              csvContent += `${date},"${athleteName}",${status},"${notes}"\n`;
            } else {
              csvContent += `${date},"${athleteName}",${status}\n`;
            }
          });
        });
      }
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `athlete-attendance-${reportType}-report-${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else if (format === 'excel') {
      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      
      if (reportType === 'summary' || reportType === 'grid') {
        const filtered = getFilteredAttendance();
        const dates = [...new Set(filtered.map(r => r.date))].sort((a, b) => new Date(a) - new Date(b));
        const statusMap = {};
        filtered.forEach(r => {
          if (!statusMap[r.athleteId]) statusMap[r.athleteId] = {};
          statusMap[r.athleteId][r.date] = r.status;
        });
        const statusLetter = (s) => s === 'present' ? 'P' : s === 'absent' ? 'A' : s === 'late' ? 'L' : s === 'excused' ? 'E' : '';

        // Info sheet
        const summaryData = [
          ['Attendance Summary Report'],
          ['Program', programName],
          ['Date Range', dateRangeLabel],
          ['Generated', new Date().toLocaleString()],
          [''],
          ['Total Records', stats.total],
          ['Attendance Rate', `${stats.attendanceRate}%`],
          ['Total Athletes', stats.uniqueAthletes],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Info');

        // Grid sheet
        const headerRow = ['Athlete', 'Present', 'Absent', 'Late', 'Excused', '%', ...dates];
        const gridData = [headerRow];
        athleteStats.forEach(a => {
          const ad = statusMap[a.id] || {};
          gridData.push([a.name, a.present, a.absent, a.late, a.excused, a.rate, ...dates.map(d => statusLetter(ad[d]))]);
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(gridData), 'Attendance Grid');
      } else {
        // Detailed Report Excel
        const headerRow = includeNotes 
          ? ['Date', 'Athlete', 'Program', 'Status', 'Notes']
          : ['Date', 'Athlete', 'Program', 'Status'];
        
        const detailedData = [headerRow];
        
        uniqueDates.forEach(date => {
          const records = byDate[date];
          records.forEach(record => {
            const athlete = athletes.find(a => a.id === record.athleteId);
            const program = programs.find(p => p.id === record.programId);
            const row = [
              date,
              athlete?.name || 'Unknown',
              program?.name || 'Unknown',
              getStatusDisplay(record.status).label
            ];
            if (includeNotes) {
              row.push(record.notes || '');
            }
            detailedData.push(row);
          });
        });
        
        const detailedWs = XLSX.utils.aoa_to_sheet(detailedData);
        XLSX.utils.book_append_sheet(wb, detailedWs, 'Detailed Attendance');
        
        // Also add summary sheet
        const summaryData = [
          ['Report Info'],
          ['Program', programName],
          ['Date Range', dateRangeLabel],
          ['Total Records', stats.total],
          ['Attendance Rate', `${stats.attendanceRate}%`]
        ];
        const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, summaryWs, 'Info');
      }
      
      // Download
      XLSX.writeFile(wb, `athlete-attendance-${reportType}-report-${dateStr}.xlsx`);
    } else if (format === 'pdf') {
      
      if (reportType === 'summary' || reportType === 'grid') {
        const filtered = getFilteredAttendance();
        const dates = [...new Set(filtered.map(r => r.date))].sort((a, b) => new Date(a) - new Date(b));
        const statusMap = {};
        filtered.forEach(r => {
          if (!statusMap[r.athleteId]) statusMap[r.athleteId] = {};
          statusMap[r.athleteId][r.date] = r.status;
        });
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const fmtDate = (d) => {
          const dt = new Date(d + 'T12:00:00');
          return `${dayNames[dt.getDay()]} ${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')}`;
        };
        const statusLetter = (s) => s === 'present' ? 'P' : s === 'absent' ? 'A' : s === 'late' ? 'L' : s === 'excused' ? 'E' : '-';

        // Use landscape if > 5 date columns
        const useLandscape = dates.length > 5;
        const doc = new jsPDF(useLandscape ? { orientation: 'landscape' } : undefined);

        // Logo + header
        const textX = logoBase64 ? 46 : 14;
        addLogoToPdf(doc, 14, 8);
        doc.setFontSize(16);
        doc.setTextColor(46, 62, 111);
        doc.text('Attendance Summary Report', textX, 16);
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`${programName}  |  ${dateRangeLabel}  |  Generated: ${new Date().toLocaleDateString()}`, textX, 22);

        // Summary stats row
        const statsStartY = logoBase64 ? 40 : 26;
        autoTable(doc, {
          startY: statsStartY,
          head: [['Sessions', 'Athletes', 'Present', 'Absent', 'Late', 'Excused', 'Rate']],
          body: [[stats.total, stats.uniqueAthletes, stats.present, stats.absent, stats.late, stats.excused, `${stats.attendanceRate}%`]],
          theme: 'grid',
          headStyles: { fillColor: [46, 62, 111], fontSize: 8, cellPadding: 2 },
          bodyStyles: { fontSize: 9, cellPadding: 2, halign: 'center', fontStyle: 'bold' },
          margin: { left: 14, right: 14 }
        });

        // Legend
        const legendY = (doc.lastAutoTable?.finalY || 40) + 2;
        doc.setFontSize(7);
        doc.setTextColor(130);
        doc.text('Legend:  P = Present  |  A = Absent  |  L = Late  |  E = Excused', 14, legendY);

        // Attendance grid table
        const gridStartY = legendY + 4;
        const gridHead = [['Athlete', 'P', 'A', 'L', 'E', '%', ...dates.map(d => fmtDate(d))]];
        const gridBody = athleteStats.map(a => {
          const ad = statusMap[a.id] || {};
          return [a.name, a.present, a.absent, a.late, a.excused, a.rate, ...dates.map(d => statusLetter(ad[d]))];
        });

        autoTable(doc, {
          startY: gridStartY,
          head: gridHead,
          body: gridBody,
          theme: 'grid',
          headStyles: { fillColor: [46, 62, 111], fontSize: 7, cellPadding: 1.5, halign: 'center' },
          bodyStyles: { fontSize: 8, cellPadding: 1.5, halign: 'center' },
          columnStyles: {
            0: { halign: 'left', cellWidth: useLandscape ? 40 : 35 },
            1: { cellWidth: 10 },
            2: { cellWidth: 10 },
            3: { cellWidth: 10 },
            4: { cellWidth: 10 },
            5: { cellWidth: 12 },
          },
          margin: { left: 14, right: 14 },
          styles: { overflow: 'linebreak' },
          didParseCell: function(data) {
            if (data.section === 'body' && data.column.index >= 6) {
              const val = data.cell.raw;
              if (val === 'P') { data.cell.styles.textColor = [0, 122, 61]; data.cell.styles.fontStyle = 'bold'; }
              else if (val === 'A') { data.cell.styles.textColor = [206, 17, 38]; data.cell.styles.fontStyle = 'bold'; }
              else if (val === 'L') { data.cell.styles.textColor = [180, 140, 0]; data.cell.styles.fontStyle = 'bold'; }
              else if (val === 'E') { data.cell.styles.textColor = [0, 103, 185]; data.cell.styles.fontStyle = 'bold'; }
              else { data.cell.styles.textColor = [180, 180, 180]; }
            }
            if (data.section === 'body') {
              if (data.column.index === 1) data.cell.styles.textColor = [0, 122, 61];
              if (data.column.index === 2) data.cell.styles.textColor = [206, 17, 38];
              if (data.column.index === 3) data.cell.styles.textColor = [180, 140, 0];
              if (data.column.index === 4) data.cell.styles.textColor = [0, 103, 185];
              if (data.column.index === 5) {
                const rate = Number(data.cell.raw);
                data.cell.styles.fontStyle = 'bold';
                if (rate >= 80) data.cell.styles.textColor = [0, 122, 61];
                else if (rate >= 60) data.cell.styles.textColor = [180, 140, 0];
                else data.cell.styles.textColor = [206, 17, 38];
              }
            }
          }
        });

        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            'Athlete Attendance App - www.attendancetracker.ws',
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 8,
            { align: 'center' }
          );
        }
        doc.save(`athlete-attendance-summary-report-${dateStr}.pdf`);

      } else {
        // Detailed Report PDF
        const doc = new jsPDF();
        
        const textX = logoBase64 ? 46 : 14;
        addLogoToPdf(doc, 14, 8);
        doc.setFontSize(20);
        doc.setTextColor(46, 62, 111);
        doc.text('Detailed Attendance Report', textX, 22);
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text(`Program: ${programName}`, 14, 42);
        doc.text(`Date Range: ${dateRangeLabel}`, 14, 48);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 54);

        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('Detailed Attendance Records', 14, 64);
        
        const tableHead = includeNotes 
          ? [['Date', 'Athlete', 'Status', 'Notes']]
          : [['Date', 'Athlete', 'Status']];
        
        const tableBody = [];
        uniqueDates.forEach(date => {
          const records = byDate[date];
          records.forEach(record => {
            const athlete = athletes.find(a => a.id === record.athleteId);
            const row = [
              date,
              athlete?.name || 'Unknown',
              getStatusDisplay(record.status).label
            ];
            if (includeNotes) {
              row.push(record.notes || '-');
            }
            tableBody.push(row);
          });
        });
        
        autoTable(doc, {
          startY: 68,
          head: tableHead,
          body: tableBody,
          theme: 'striped',
          headStyles: { fillColor: [46, 62, 111] },
          columnStyles: includeNotes ? { 3: { cellWidth: 50 } } : {},
          styles: { fontSize: 9 }
        });

        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(10);
          doc.setTextColor(150);
          doc.text(
            'Athlete Attendance App - www.attendancetracker.ws',
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 10,
            { align: 'center' }
          );
        }
        doc.save(`athlete-attendance-detailed-report-${dateStr}.pdf`);
      }
    }
  };

  const handleExportAthletesByProgram = async () => {
    try {
      const params = selectedProgram !== 'all' ? `?program_id=${selectedProgram}` : '';
      const response = await axios.get(`${API}/csv/export-athletes-by-program${params}`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const programLabel = selectedProgram === 'all'
        ? 'all_programs'
        : (programs.find(p => p.id === selectedProgram)?.name || 'program').replace(/\s+/g, '_');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `athletes_${programLabel}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting athletes:', error);
      alert('Error exporting athletes. Please try again.');
    }
  };

  // Get athletes filtered by selected program
  const getFilteredAthletes = () => {
    const list = selectedProgram === 'all' ? athletes : athletes.filter(a => a.programs?.includes(selectedProgram));
    return [...list].sort((a, b) => {
      const lastA = (a.name || '').trim().split(/\s+/).pop().toLowerCase();
      const lastB = (b.name || '').trim().split(/\s+/).pop().toLowerCase();
      return lastA.localeCompare(lastB);
    });
  };

  // Individual athlete report data
  const getAthleteReportData = () => {
    if (selectedAthlete === 'all') return null;
    const athlete = athletes.find(a => a.id === selectedAthlete);
    if (!athlete) return null;

    const filtered = getFilteredAttendance();
    // Group by program
    const byProgram = {};
    filtered.forEach(r => {
      const progName = programs.find(p => p.id === r.programId)?.name || 'Unknown';
      if (!byProgram[progName]) byProgram[progName] = [];
      byProgram[progName].push(r);
    });

    // Overall stats
    const present = filtered.filter(r => r.status === 'present').length;
    const absent = filtered.filter(r => r.status === 'absent').length;
    const late = filtered.filter(r => r.status === 'late').length;
    const excused = filtered.filter(r => r.status === 'excused').length;
    const total = filtered.length;
    const rate = total > 0 ? Math.round(((present + late) / total) * 100) : 0;

    return { athlete, records: filtered, byProgram, stats: { present, absent, late, excused, total, rate } };
  };

  const handleExportAthleteReport = (format) => {
    const data = getAthleteReportData();
    if (!data) return;
    const { athlete, records, byProgram, stats: aStats } = data;
    const dateStr = new Date().toISOString().split('T')[0];
    const dateRangeLabel = getDateRangeLabel();
    const safeName = athlete.name.replace(/\s+/g, '_');

    if (format === 'csv') {
      let csv = `Athlete Attendance Report\n`;
      csv += `Name,${athlete.name}\n`;
      if (athlete.email) csv += `Email,${athlete.email}\n`;
      if (athlete.phone) csv += `Phone,${athlete.phone}\n`;
      csv += `Date Range,${dateRangeLabel}\n`;
      csv += `Attendance Rate,${aStats.rate}%\n`;
      csv += `Total,${aStats.total},Present,${aStats.present},Late,${aStats.late},Absent,${aStats.absent},Excused,${aStats.excused}\n\n`;
      csv += `Date,Program,Status${includeNotes ? ',Notes' : ''}\n`;
      records.sort((a, b) => new Date(b.date) - new Date(a.date)).forEach(r => {
        const prog = programs.find(p => p.id === r.programId)?.name || '';
        const status = getStatusDisplay(r.status).label;
        csv += `${r.date},"${prog}",${status}${includeNotes ? `,"${(r.notes || '').replace(/"/g, '""')}"` : ''}\n`;
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_attendance_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } else if (format === 'excel') {
      const wb = XLSX.utils.book_new();
      const infoData = [
        ['Athlete Attendance Report'],
        ['Name', athlete.name],
        ...(athlete.email ? [['Email', athlete.email]] : []),
        ...(athlete.phone ? [['Phone', athlete.phone]] : []),
        ['Date Range', dateRangeLabel],
        [''],
        ['Metric', 'Count'],
        ['Total Sessions', aStats.total],
        ['Present', aStats.present],
        ['Late', aStats.late],
        ['Absent', aStats.absent],
        ['Excused', aStats.excused],
        ['Attendance Rate', `${aStats.rate}%`],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(infoData), 'Summary');

      const headerRow = includeNotes ? ['Date', 'Program', 'Status', 'Notes'] : ['Date', 'Program', 'Status'];
      const detailData = [headerRow];
      records.sort((a, b) => new Date(b.date) - new Date(a.date)).forEach(r => {
        const row = [r.date, programs.find(p => p.id === r.programId)?.name || '', getStatusDisplay(r.status).label];
        if (includeNotes) row.push(r.notes || '');
        detailData.push(row);
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(detailData), 'Attendance Records');
      XLSX.writeFile(wb, `${safeName}_attendance_${dateStr}.xlsx`);
    } else if (format === 'pdf') {
      const doc = new jsPDF();
      // Logo + compact header
      const textX = logoBase64 ? 46 : 14;
      addLogoToPdf(doc, 14, 8);
      doc.setFontSize(16);
      doc.setTextColor(46, 62, 111);
      doc.text(`${athlete.name} — Attendance Report`, textX, 16);
      doc.setFontSize(9);
      doc.setTextColor(100);
      const infoLine = [
        athlete.email, athlete.phone,
        `${dateRangeLabel}`,
        `Generated: ${new Date().toLocaleDateString()}`
      ].filter(Boolean).join('  |  ');
      doc.text(infoLine, textX, 22);

      // Compact stats in a single row table
      const athleteStatsY = logoBase64 ? 40 : 26;
      autoTable(doc, {
        startY: athleteStatsY,
        head: [['Sessions', 'Present', 'Late', 'Absent', 'Excused', 'Rate']],
        body: [[aStats.total, aStats.present, aStats.late, aStats.absent, aStats.excused, `${aStats.rate}%`]],
        theme: 'grid',
        headStyles: { fillColor: [46, 62, 111], fontSize: 8, cellPadding: 2 },
        bodyStyles: { fontSize: 9, cellPadding: 2, halign: 'center', fontStyle: 'bold' },
        columnStyles: { 5: { textColor: aStats.rate >= 80 ? [0, 122, 61] : aStats.rate >= 60 ? [180, 140, 0] : [206, 17, 38] } },
        margin: { left: 14, right: 14 }
      });

      // Attendance records table — compact
      const tableStartY = (doc.lastAutoTable?.finalY || 40) + 4;
      const tableHead = includeNotes
        ? [['Date', 'Program', 'Status', 'Notes']]
        : [['Date', 'Program', 'Status']];
      const tableBody = [];
      records.sort((a, b) => new Date(b.date) - new Date(a.date)).forEach(r => {
        const row = [r.date, programs.find(p => p.id === r.programId)?.name || '', getStatusDisplay(r.status).label];
        if (includeNotes) row.push(r.notes || '');
        tableBody.push(row);
      });

      autoTable(doc, {
        startY: tableStartY,
        head: tableHead,
        body: tableBody,
        theme: 'striped',
        headStyles: { fillColor: [46, 62, 111], fontSize: 8, cellPadding: 1.5 },
        bodyStyles: { fontSize: 8, cellPadding: 1.5 },
        columnStyles: includeNotes ? { 3: { cellWidth: 50 } } : {},
        margin: { left: 14, right: 14 },
        styles: { overflow: 'linebreak' }
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          'Athlete Attendance App — www.attendancetracker.ws',
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 8,
          { align: 'center' }
        );
      }
      doc.save(`${safeName}_attendance_${dateStr}.pdf`);
    }
  };

  const stats = calculateStats();
  const athleteStats = getAthleteStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">View attendance insights and trends</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleExportAthletesByProgram}
            className="flex items-center"
            data-testid="export-athletes-btn"
          >
            <Users className="h-4 w-4 mr-2" />
            {selectedProgram === 'all' ? 'Export All Athletes' : `Export ${programs.find(p => p.id === selectedProgram)?.name || 'Program'} Athletes`}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                style={{ backgroundColor: '#0067B9' }}
                className="hover:opacity-90"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => reportType === 'athlete' ? handleExportAthleteReport('pdf') : handleExport('pdf')}>
                <FileText className="h-4 w-4 mr-2 text-red-600" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => reportType === 'athlete' ? handleExportAthleteReport('excel') : handleExport('excel')}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                Export as Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => reportType === 'athlete' ? handleExportAthleteReport('csv') : handleExport('csv')}>
                <Download className="h-4 w-4 mr-2 text-blue-600" />
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Program</label>
              <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programs</SelectItem>
                  {programs.map(program => (
                    <SelectItem key={program.id} value={program.id}>
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Date Range</label>
              <Select value={dateRange} onValueChange={(value) => {
                setDateRange(value);
                if (value !== 'custom') {
                  setCustomStartDate('');
                  setCustomEndDate('');
                }
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 6 months</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                  <SelectItem value="custom">
                    <span className="flex items-center">
                      <CalendarRange className="h-4 w-4 mr-2" />
                      Custom Range
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Report Type</label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">
                    <span className="flex items-center">
                      <ListChecks className="h-4 w-4 mr-2" />
                      Summary Report
                    </span>
                  </SelectItem>
                  <SelectItem value="detailed">
                    <span className="flex items-center">
                      <ClipboardList className="h-4 w-4 mr-2" />
                      Detailed Report
                    </span>
                  </SelectItem>
                  <SelectItem value="athlete">
                    <span className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Individual Athlete
                    </span>
                  </SelectItem>
                  <SelectItem value="grid">
                    <span className="flex items-center">
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Attendance Grid
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reportType === 'detailed' && (
              <div className="flex items-end">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-notes"
                    checked={includeNotes}
                    onCheckedChange={setIncludeNotes}
                    data-testid="include-notes-checkbox"
                  />
                  <label
                    htmlFor="include-notes"
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Include session notes
                  </label>
                </div>
              </div>
            )}
            {reportType === 'athlete' && (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Athlete</label>
                  <Popover open={athleteSearchOpen} onOpenChange={setAthleteSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={athleteSearchOpen}
                        className="w-full justify-between font-normal"
                        data-testid="athlete-select"
                      >
                        {selectedAthlete === 'all'
                          ? '-- Select Athlete --'
                          : getFilteredAthletes().find(a => a.id === selectedAthlete)?.name || '-- Select Athlete --'}
                        <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/></svg>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search athlete..." data-testid="athlete-search-input" />
                        <CommandList>
                          <CommandEmpty>No athlete found.</CommandEmpty>
                          <CommandGroup>
                            {getFilteredAthletes().map(athlete => (
                              <CommandItem
                                key={athlete.id}
                                value={athlete.name}
                                onSelect={() => {
                                  setSelectedAthlete(athlete.id);
                                  setAthleteSearchOpen(false);
                                }}
                                data-testid={`athlete-option-${athlete.id}`}
                              >
                                {athlete.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-end">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include-notes-athlete"
                      checked={includeNotes}
                      onCheckedChange={setIncludeNotes}
                    />
                    <label
                      htmlFor="include-notes-athlete"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Include notes
                    </label>
                  </div>
                </div>
              </>
            )}
            {dateRange === 'custom' && (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Start Date</label>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || undefined}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">End Date</label>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate || undefined}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </>
            )}
          </div>
          {dateRange === 'custom' && (!customStartDate || !customEndDate) && (
            <p className="text-sm text-amber-600 mt-3 flex items-center">
              <CalendarRange className="h-4 w-4 mr-2" />
              Please select both start and end dates to view results
            </p>
          )}
          {reportType === 'detailed' && (
            <p className="text-sm text-blue-600 mt-3 flex items-center">
              <ClipboardList className="h-4 w-4 mr-2" />
              Detailed report shows individual attendance records with dates and statuses for each athlete
            </p>
          )}
        </CardContent>
      </Card>

      {/* Overall Statistics - hide when grid or individual athlete report is shown */}
      {reportType !== 'athlete' && reportType !== 'grid' && (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4" style={{ borderLeftColor: '#007A3D' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#007A3D' }}>{stats.total}</div>
            <p className="text-xs text-gray-500 mt-1">Attendance records</p>
          </CardContent>
        </Card>

        <Card className="border-l-4" style={{ borderLeftColor: '#6B21A8' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Athletes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#6B21A8' }} data-testid="total-athletes-stat">{stats.uniqueAthletes}</div>
            <p className="text-xs text-gray-500 mt-1">Unique athletes tracked</p>
          </CardContent>
        </Card>

        <Card className="border-l-4" style={{ borderLeftColor: '#0067B9' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Attendance Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#0067B9' }}>{stats.attendanceRate}%</div>
            <p className="text-xs text-gray-500 mt-1">Present + Late</p>
          </CardContent>
        </Card>

        <Card className="border-l-4" style={{ borderLeftColor: '#CE1126' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Absences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#CE1126' }}>{stats.absent}</div>
            <p className="text-xs text-gray-500 mt-1">Total absent records</p>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Attendance Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Present</span>
                <span className="text-sm font-semibold" style={{ color: '#007A3D' }}>
                  {stats.present} ({stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all"
                  style={{
                    backgroundColor: '#007A3D',
                    width: `${stats.total > 0 ? (stats.present / stats.total) * 100 : 0}%`
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Late</span>
                <span className="text-sm font-semibold" style={{ color: '#FCD116' }}>
                  {stats.late} ({stats.total > 0 ? Math.round((stats.late / stats.total) * 100) : 0}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all"
                  style={{
                    backgroundColor: '#FCD116',
                    width: `${stats.total > 0 ? (stats.late / stats.total) * 100 : 0}%`
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Absent</span>
                <span className="text-sm font-semibold" style={{ color: '#CE1126' }}>
                  {stats.absent} ({stats.total > 0 ? Math.round((stats.absent / stats.total) * 100) : 0}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all"
                  style={{
                    backgroundColor: '#CE1126',
                    width: `${stats.total > 0 ? (stats.absent / stats.total) * 100 : 0}%`
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Excused</span>
                <span className="text-sm font-semibold" style={{ color: '#0067B9' }}>
                  {stats.excused} ({stats.total > 0 ? Math.round((stats.excused / stats.total) * 100) : 0}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all"
                  style={{
                    backgroundColor: '#0067B9',
                    width: `${stats.total > 0 ? (stats.excused / stats.total) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Attendance Grid - shown for summary and grid report types */}
      {(reportType === 'summary' || reportType === 'grid') && (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-xl">Attendance Grid</CardTitle>
            <div className="flex items-center gap-3 text-xs text-gray-500" data-testid="attendance-legend">
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> P = Present</span>
              <span className="flex items-center gap-1"><XCircle className="h-3.5 w-3.5 text-red-500" /> A = Absent</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-yellow-500" /> L = Late</span>
              <span className="flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5 text-blue-500" /> E = Excused</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {athleteStats.length > 0 ? (() => {
            const filtered = getFilteredAttendance();
            // Get unique dates sorted ascending (left to right chronologically)
            const dates = [...new Set(filtered.map(r => r.date))].sort((a, b) => new Date(a) - new Date(b));
            // Build lookup: athleteId -> { date -> status }
            const statusMap = {};
            filtered.forEach(r => {
              if (!statusMap[r.athleteId]) statusMap[r.athleteId] = {};
              statusMap[r.athleteId][r.date] = r.status;
            });
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const formatDateHeader = (d) => {
              const dt = new Date(d + 'T12:00:00');
              const day = dayNames[dt.getDay()];
              const mm = String(dt.getMonth() + 1).padStart(2, '0');
              const dd = String(dt.getDate()).padStart(2, '0');
              return { day, date: `${mm}/${dd}` };
            };

            const statusIcon = (status) => {
              if (status === 'present') return <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />;
              if (status === 'absent') return <XCircle className="h-5 w-5 text-red-500 mx-auto" />;
              if (status === 'late') return <Clock className="h-5 w-5 text-yellow-500 mx-auto" />;
              if (status === 'excused') return <AlertCircle className="h-5 w-5 text-blue-500 mx-auto" />;
              return <span className="text-gray-300 mx-auto">—</span>;
            };

            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="attendance-grid-table">
                  <thead>
                    {/* Header row with program info + summary + dates */}
                    <tr className="bg-gray-50 border-b-2 border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[140px]">
                        {selectedProgram !== 'all'
                          ? programs.find(p => p.id === selectedProgram)?.name || 'Program'
                          : 'All Programs'}
                      </th>
                      <th className="text-center py-2 px-1.5 min-w-[36px]" title="Present">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                        <span className="text-xs font-bold text-green-700">{stats.present}</span>
                      </th>
                      <th className="text-center py-2 px-1.5 min-w-[36px]" title="Absent">
                        <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                        <span className="text-xs font-bold text-red-700">{stats.absent}</span>
                      </th>
                      <th className="text-center py-2 px-1.5 min-w-[36px]" title="Late">
                        <Clock className="h-4 w-4 text-yellow-500 mx-auto" />
                        <span className="text-xs font-bold text-yellow-700">{stats.late}</span>
                      </th>
                      <th className="text-center py-2 px-1.5 min-w-[36px]" title="Excused">
                        <AlertCircle className="h-4 w-4 text-blue-500 mx-auto" />
                        <span className="text-xs font-bold text-blue-700">{stats.excused}</span>
                      </th>
                      <th className="text-center py-2 px-1.5 min-w-[36px]" title="Attendance Rate">
                        <span className="text-xs font-bold text-gray-700">%</span>
                      </th>
                      {dates.map(d => {
                        const hdr = formatDateHeader(d);
                        return (
                          <th key={d} className="text-center py-2 px-1.5 min-w-[52px]">
                            <div className="text-[10px] text-gray-500 leading-tight">{hdr.day}</div>
                            <div className="text-[10px] font-medium text-gray-700 leading-tight">{hdr.date}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {athleteStats.map((athlete) => {
                      const athleteDates = statusMap[athlete.id] || {};
                      return (
                        <tr key={athlete.id} className="border-b hover:bg-gray-50/60">
                          <td className="py-2 px-3 sticky left-0 bg-white z-10">
                            <div className="flex items-center gap-2">
                              <AthleteAvatar athlete={athlete} size="sm" />
                              <span className="font-medium text-gray-800 text-sm leading-tight">{athlete.name}</span>
                            </div>
                          </td>
                          <td className="text-center py-2 px-1.5 text-sm font-semibold text-green-700">{athlete.present}</td>
                          <td className="text-center py-2 px-1.5 text-sm font-semibold text-red-700">{athlete.absent}</td>
                          <td className="text-center py-2 px-1.5 text-sm font-semibold text-yellow-700">{athlete.late}</td>
                          <td className="text-center py-2 px-1.5 text-sm font-semibold text-blue-700">{athlete.excused}</td>
                          <td className="text-center py-2 px-1.5 text-sm font-bold" style={{ color: athlete.rate >= 80 ? '#007A3D' : athlete.rate >= 60 ? '#B8860B' : '#CE1126' }}>{athlete.rate}</td>
                          {dates.map(d => (
                            <td key={d} className="text-center py-2 px-1.5">
                              {statusIcon(athleteDates[d])}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })() : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">No data available</p>
              <p className="text-sm mt-1">Start taking attendance to see reports</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Detailed Attendance Records - shown when "Detailed Report" is selected */}
      {reportType === 'detailed' && (
        <Card data-testid="detailed-attendance-card">
          <CardHeader>
            <CardTitle className="text-xl flex items-center">
              <ClipboardList className="h-5 w-5 mr-2" />
              Detailed Attendance Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const { uniqueDates, byDate } = getDetailedAttendance();
              
              if (uniqueDates.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">No attendance records found</p>
                    <p className="text-sm mt-1">Adjust your filters to see detailed records</p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-6">
                  {uniqueDates.map(date => {
                    const records = byDate[date];
                    const formattedDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    });
                    
                    return (
                      <div key={date} className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b">
                          <h3 className="font-semibold text-gray-800 flex items-center">
                            <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                            {formattedDate}
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              ({records.length} {records.length === 1 ? 'record' : 'records'})
                            </span>
                          </h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b bg-gray-50/50">
                                <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Athlete</th>
                                <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Program</th>
                                <th className="text-center py-2 px-4 font-medium text-gray-600 text-sm">Status</th>
                                {includeNotes && (
                                  <th className="text-left py-2 px-4 font-medium text-gray-600 text-sm">Notes</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {records.map((record, idx) => {
                                const athlete = athletes.find(a => a.id === record.athleteId);
                                const program = programs.find(p => p.id === record.programId);
                                const statusDisplay = getStatusDisplay(record.status);
                                
                                return (
                                  <tr key={record.id || idx} className="border-b last:border-b-0 hover:bg-gray-50">
                                    <td className="py-2 px-4">
                                      <div className="flex items-center gap-2">
                                        <AthleteAvatar athlete={athlete} size="sm" />
                                        <span className="font-medium text-gray-800">{athlete?.name || 'Unknown'}</span>
                                      </div>
                                    </td>
                                    <td className="py-2 px-4">
                                      <span className="text-gray-600">{program?.name || 'Unknown'}</span>
                                    </td>
                                    <td className="text-center py-2 px-4">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDisplay.bg}`}>
                                        {statusDisplay.label}
                                      </span>
                                    </td>
                                    {includeNotes && (
                                      <td className="py-2 px-4">
                                        <span className="text-sm text-gray-600 italic">
                                          {record.notes || '-'}
                                        </span>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Individual Athlete Report */}
      {reportType === 'athlete' && (() => {
        if (selectedAthlete === 'all') {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500">Select an athlete</p>
                <p className="text-sm text-gray-400 mt-1">Choose an athlete from the filter above to view their individual report</p>
              </CardContent>
            </Card>
          );
        }

        const data = getAthleteReportData();
        if (!data) return null;
        const { athlete, records, byProgram, stats: aStats } = data;

        return (
          <div className="space-y-4" data-testid="athlete-report-section">
            {/* Athlete header card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <AthleteAvatar athlete={athlete} size="lg" />
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{athlete.name}</h2>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                        {athlete.email && <span>{athlete.email}</span>}
                        {athlete.phone && <span>{athlete.phone}</span>}
                        {athlete.age > 0 && <span>Age {athlete.age}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className="text-3xl font-bold"
                      style={{ color: aStats.rate >= 80 ? '#007A3D' : aStats.rate >= 60 ? '#FCD116' : '#CE1126' }}
                    >
                      {aStats.rate}%
                    </span>
                    <p className="text-xs text-gray-500">Attendance Rate</p>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-3 mt-4">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-lg font-bold text-gray-800">{aStats.total}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="text-center p-2 bg-green-50 rounded">
                    <p className="text-lg font-bold" style={{ color: '#007A3D' }}>{aStats.present}</p>
                    <p className="text-xs text-gray-500">Present</p>
                  </div>
                  <div className="text-center p-2 bg-yellow-50 rounded">
                    <p className="text-lg font-bold" style={{ color: '#B8860B' }}>{aStats.late}</p>
                    <p className="text-xs text-gray-500">Late</p>
                  </div>
                  <div className="text-center p-2 bg-red-50 rounded">
                    <p className="text-lg font-bold" style={{ color: '#CE1126' }}>{aStats.absent}</p>
                    <p className="text-xs text-gray-500">Absent</p>
                  </div>
                  <div className="text-center p-2 bg-blue-50 rounded">
                    <p className="text-lg font-bold" style={{ color: '#0067B9' }}>{aStats.excused}</p>
                    <p className="text-xs text-gray-500">Excused</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Attendance records table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Attendance Records</CardTitle>
              </CardHeader>
              <CardContent>
                {records.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p>No attendance records found for this date range.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Date</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-600">Program</th>
                          <th className="text-center py-2 px-3 font-semibold text-gray-600">Status</th>
                          {includeNotes && <th className="text-left py-2 px-3 font-semibold text-gray-600">Notes</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {records.sort((a, b) => new Date(b.date) - new Date(a.date)).map((record, idx) => {
                          const prog = programs.find(p => p.id === record.programId);
                          const statusDisplay = getStatusDisplay(record.status);
                          return (
                            <tr key={record.id || idx} className="border-b last:border-b-0 hover:bg-gray-50">
                              <td className="py-2 px-3 text-gray-700">{record.date}</td>
                              <td className="py-2 px-3 text-gray-600">{prog?.name || 'Unknown'}</td>
                              <td className="text-center py-2 px-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusDisplay.bg}`}>
                                  {statusDisplay.label}
                                </span>
                              </td>
                              {includeNotes && (
                                <td className="py-2 px-3 text-gray-500 italic text-xs">{record.notes || '-'}</td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}
    </div>
  );
};

export default Reports;